/**
 * A identidade visual do 4ESC Tech dentro do CreditOS: o nome, a pele escura e
 * as letras da marca.
 *
 * ── Por que mora aqui, e não na página nem no menu ──────────────────────────
 * Os dois usam, e nenhum dos dois pode importar o outro. A página é carregada
 * sob demanda (`lazy` no router). Se o atalho do menu, que aparece em toda tela,
 * importasse dela, a página inteira entraria no pacote de toda tela, e as duas
 * passariam a se importar em círculo. Ciclo de importação com constante é o
 * caminho do "Cannot access X before initialization", que já chegou à tela de
 * cliente nesta casa. O teste do atalho reprova se ele voltar a importar a página.
 */

/** Como o parceiro se chama na tela. */
export const NOME_DO_PARCEIRO = "4ESC Tech"

/**
 * A pele escura do 4ESC Tech: o fundo do site deles (#030712) com o fio índigo
 * (#818CF8) do botão secundário deles. A placa do logo, os dois botões da página
 * e o atalho do menu usam ESTA constante, não cópias dela: são a mesma
 * identidade e não podem divergir por alguém mexer só num lugar.
 *
 * Medido sobre as cores reais: texto branco no botão da página, 20,1 : 1 (no
 * hover, sobre #111827, 17,7); o botão contra o cartão branco, 20 : 1. O piso da
 * WCAG é 4,5 para texto e 3 para o contorno de um controle.
 *
 * Sem variante `dark:`, também de propósito. O tema escuro está desligado hoje
 * (o `useTheme` força o claro). Quando voltar, a pele continua sendo o chão do
 * logo e das letras nos dois temas. O que perde força no escuro é o fundo contra
 * a superfície em volta; o destaque passa a ser carregado pela letra colorida e
 * pelo contorno, que continuam claros sobre qualquer escuro.
 */
export const PELE_DO_4ESC = "bg-[#030712] border border-[rgba(129,140,248,0.45)]"

/**
 * As letras da marca, do jeito que a 4ESC escreve "4ESC Tech" no site deles: sem
 * serifa, bem grossas, justas, e com um degradê horizontal tirado da paleta do
 * Tailwind, a mesma do site deles (o fundo #030712 e o fio #818CF8 também são).
 *
 *   #818CF8  indigo-400   começo, no "4"
 *   #A78BFA  violet-400   meio, no "ESC"
 *   #F0ABFC  fuchsia-300  fim, no último "h" de "Tech"
 *
 * O fim é mais rosado que a cor lida no meio do "Tech" da amostra (#D8A0F4) de
 * propósito: o meio de uma palavra mostra a MISTURA do caminho, não a ponta. Com
 * estas três paradas, o meio do "Tech" sai em #C799FB e o meio do "ESC" em
 * #968BF9. Se o olho do PM pedir outro tom, a troca é uma cor nesta linha.
 *
 * 🚨 A letra é TRANSPARENTE, e quem pinta é o fundo recortado no desenho de cada
 * letra (`bg-clip-text`). Se a classe do degradê deixar de virar CSS, o nome
 * some: sobra letra transparente sobre fundo escuro, sem erro nenhum. É por isso
 * que o teste do atalho compila estas classes com o Tailwind de verdade.
 *
 * Contraste no pior ponto do degradê (o "4", índigo), medido sobre cada fundo que
 * o atalho assume: 6,75 : 1 sobre #030712 (normal), 5,95 sobre #111827 (mouse em
 * cima) e 5,36 sobre #1E1B4B (página do parceiro aberta). Todos acima de 4,5, o
 * piso da WCAG para texto de qualquer tamanho, e não só o de texto grande.
 *
 * A fonte: nenhuma baixada de fora. É a pilha do próprio app com `system-ui`
 * antes do genérico. Onde o app já tem fonte com peso pesado (SF no Mac e no
 * iPhone, Inter onde estiver instalada), nada muda. Onde o app cairia no genérico
 * (Arial no Windows, que só vai até o negrito 700), entra a fonte do sistema, que
 * tem peso pesado de verdade: Segoe UI Black no Windows, Roboto Black no Android.
 * Peso 800 com espaçamento justo é o que mais se aproxima da letra da amostra.
 */
export const LETRAS_DO_4ESC =
  "bg-[linear-gradient(90deg,#818CF8,#A78BFA,#F0ABFC)] bg-clip-text text-transparent " +
  "font-extrabold tracking-tight " +
  "[font-family:-apple-system,BlinkMacSystemFont,SF_Pro_Display,Inter,system-ui,sans-serif]"
